import { useRef, useState } from 'react';
import { X, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Download } from 'lucide-react';
import { readCoaFile, buildImport, coaTemplateCsv, CoaImportResult, ImportIssue } from '../lib/coaImport';
import type { CoaAccount } from './CoaPreview';
import { tr } from '../lib/i18nHelpers';

interface Props {
  show: boolean;
  onApply: (accounts: CoaAccount[], balances: Record<string, number>) => void;
  onClose: () => void;
  templateAccounts?: CoaAccount[];
}

export default function CoaImportModal({ show, onApply, onClose, templateAccounts }: Props) {
  const [result, setResult] = useState<CoaImportResult | null>(null);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  if (!show) return null;

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setResult(null);
    try {
      const rows = await readCoaFile(file);
      setResult(buildImport(rows));
    } catch (e: any) {
      setResult({ accounts: [], errors: [new ImportIssue(null, e?.message || String(e))], warnings: [] });
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([coaTemplateCsv(templateAccounts)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'coa-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const hasErrors = !!result && result.errors.length > 0;
  const balances: Record<string, number> = {};
  result?.accounts.forEach(a => { if (a.opening_balance) balances[a.account_code] = a.opening_balance; });

  return (
    <div data-testid="coa-import-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            {tr('Import Chart of Accounts', '導入會計科目', '导入会计科目')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
        </div>

        <p className="text-xs text-muted-foreground">
          {tr('Column 1: account code, Column 2: account name, optional Column 3: opening balance (use brackets or a minus sign for negative, e.g. (1,234.56)). Hierarchical level is inferred from the code.', '第一欄：科目編碼；第二欄：科目名稱；第三欄（可選）：開賬結餘（負數可用括號或負號，例如 (1,234.56)）。層級由編碼推斷。', '第一列：科目编号；第二列：科目名称；第三列（可选）：开账余额（负数可用括号或负号，例如 (1,234.56)）。层级由编号推断。')}
        </p>

        <div className="flex gap-2">
          <input
            ref={inputRef} type="file" accept=".xlsx,.xls,.csv"
            className="text-xs" data-testid="coa-import-file-input"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <button type="button" onClick={downloadTemplate} className="flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-muted/50">
            <Download className="h-3 w-3" /> {tr('Download template', '下載範本', '下载范本')}
          </button>
        </div>

        {fileName && (
          <div className="text-xs text-muted-foreground">{fileName}</div>
        )}

        {result && (
          <div data-testid="coa-import-result" className="space-y-2 text-xs">
            {!hasErrors && (
              <div className="flex items-center gap-1 text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {tr(`${result.accounts.length} accounts will be imported`, `將導入 ${result.accounts.length} 個科目`, `将导入 ${result.accounts.length} 个科目`)}
              </div>
            )}
            {result.errors.map((e, i) => (
              <div key={i} className="flex items-start gap-1 text-red-600">
                <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{e.row ? tr(`Row ${e.row}: `, `第 ${e.row} 行：`, `第 ${e.row} 行：`) : ''}{e.message}</span>
              </div>
            ))}
            {result.warnings.map((w, i) => (
              <div key={i} className="flex items-start gap-1 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{w.message}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">{tr('Cancel', '取消', '取消')}</button>
          <button
            data-testid="coa-import-apply"
            disabled={!result || hasErrors || result.accounts.length === 0}
            onClick={() => onApply(result!.accounts, balances)}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
          >
            {tr('Apply Import', '套用導入', '应用导入')}
          </button>
        </div>
      </div>
    </div>
  );
}
